import React, { useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  black,
  blackText,
  blue,
  blueButton,
  cardBorder,
  gray,
  grayPlaceholder,
  tertiaryButtonBackground
} from '../../constants/Colors';
import {
  categoryList,
  mediaTargetList,
  mediaTypeList,
  useIndustry,
  funnelList,
  targetTypeList,
  percentList,
  generationList,
  accessList,
  mediaBudgetList
} from '../../constants/Select';

import CustomGuideButton from './CustomGuideButton';

import type { SelectOption, SelectOptions } from '../../constants/Select';
import type { SelectChangeEvent } from '@mui/material';

interface Props {
  search: (
    word: string,
    genre: SelectOption[],
    mediaType: SelectOptions[],
    funnel: SelectOption[],
    target: SelectOption[],
    targetType: SelectOption[],
    selectedGender:
      | {
          gender: string | null | undefined;
          percentFrom: number | null | undefined;
          percentTo: number | null | undefined;
        }
      | undefined,
    selectedGeneration:
      | {
          generation: string | null | undefined;
          percentFrom: number | null | undefined;
          percentTo: number | null | undefined;
        }
      | undefined,
    selectedAccess:
      | {
          access: number | null | undefined;
          percentFrom: number | null | undefined;
          percentTo: number | null | undefined;
        }
      | undefined,
    industry: SelectOption[],
    budget: string
  ) => void;
}

const styles = {
  blueButton,
  button: {
    '@media screen and (max-width:480px)': {
      width: '148px'
    },
    boxShadow: 0,
    height: '36px',
    width: '192px'
  },
  buttonAndChips: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '8px'
  },
  buttons: {
    '@media screen and (max-width:960px)': {
      justifyContent: 'center',
      padding: '24px 16px'
    },
    columnGap: '16px',
    rowGap: '16px'
  },
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px'
    },
    boxShadow: 0,
    margin: '24px'
  },
  cardBorder,
  cardContents: {
    '@media screen and (max-width:960px)': {
      border: '1px solid #222',
      margin: 0
    },
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    margin: '24px'
  },
  chips: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '8px'
  },
  clearButton: {
    color: black
  },
  demoTableBorder: {
    '@media screen and (max-width:700px)': {
      flexDirection: 'column'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    mb: '15px'
  },
  demoTableContent: {
    '@media screen and (max-width:700px)': {
      width: '100%'
    },
    width: '449px'
  },
  demoTableOption: {
    width: '120px'
  },
  demoTablePadding: {
    p: '12px 24px'
  },
  demoTablePercentBetween: {
    '@media screen and (max-width:700px)': {
      p: '6px 4px'
    },
    p: '6px 14px'
  },
  demoTablePercents: {
    '@media screen and (max-width:700px)': {
      p: '12px 16px'
    },
    p: '12px 24px'
  },
  demoTableText: {
    fontSize: '14px',
    fontWeight: '700',
    pl: '24px'
  },
  demoTableTextbg: {
    '@media screen and (max-width:700px)': {
      height: '44px',
      width: '100%'
    },
    bgcolor: '#F2F2F2',
    width: '140px'
  },
  demoTableWidth: {
    '@media screen and (max-width:700px)': {
      width: '311px'
    },
    width: '596px'
  },
  isActive: {
    display: 'flex'
  },
  isNotActive: {
    display: 'none'
  },
  modal: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  modalButton: {
    width: '100%'
  },
  modalOption: {
    '&.MuiFormControlLabel-root .MuiFormControlLabel-label': {
      '@media screen and (max-width:700px)': {
        fontSize: '12px'
      },
      fontSize: '14px',
      pr: '3px'
    },
    '@media screen and (max-width:700px)': {
      padding: 0
    },
    bgcolor: '#F2F2F2',
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: 0,
    padding: '0 6px',
    width: '290px'
  },
  modalOptionAllWidth: {
    width: '100%'
  },
  modalOptionWidth: {
    '@media screen and (max-width:700px)': {
      width: '151px'
    }
  },
  modalOptions: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    mb: '24px',
    rowGap: '8px'
  },
  modalText: {
    fontSize: '22px',
    padding: 0
  },
  modalTitle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0 0 24px 0'
  },
  modalWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '676px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  searchBox: {
    '@media screen and (max-width:960px)': {
      rowGap: 0
    },
    flexDirection: 'column',
    rowGap: '24px'
  },
  searchSpButton: {
    '@media screen and (max-width:960px)': {
      display: 'flex'
    },
    bgcolor: '#F2F2F2',
    color: black,
    display: 'none',
    flexDirection: 'row',
    height: '48px',
    justifyContent: 'space-between',
    padding: '12px 16px',
    textAlign: 'left'
  },
  select: {
    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: black
    },
    '@media screen and (max-width:480px)': {
      width: '100%'
    },
    height: '36px',
    padding: 0,
    width: '336px'
  },
  selectPercent: {
    '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: black
    },
    '@media screen and (max-width:700px)': {
      width: '126px'
    },
    height: '36px',
    padding: 0,
    width: '140px'
  },
  selectedChip: {
    '&.MuiChip-root .MuiChip-deleteIcon': {
      color: black
    }
  },
  table: {
    boxShadow: 0,
    maxWidth: '100%'
  },
  tableButton: {
    color: blue,
    mr: '16px',
    padding: '0px'
  },
  tableItem: {
    '@media screen and (max-width:960px)': {
      bgcolor: '#FFF',
      padding: 0
    },
    bgcolor: '#F2F2F2',
    padding: '18px 24px',
    width: '195px'
  },
  tableRow: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      padding: '12px 16px',
      rowGap: '8px'
    },
    border: '1px solid #E5E5E5',
    flexDirection: 'row',
    padding: 0,
    rowGap: 0
  },
  tableValue: {
    '@media screen and (max-width:960px)': {
      padding: 0
    },
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    padding: '12px 24px'
  },
  tertiaryButtonBackground,
  text: {
    fontSize: '14px',
    mb: '8px'
  }
};

const MediaSearchCard = (props: Props): React.ReactElement => {
  const { search } = props;
  const [searchboxIsShow, setSearchboxIsShow] = React.useState(false);
  const [budget, setBudget] = React.useState('');
  const [freeWord, setFreeWord] = React.useState('');
  const budgetList = mediaBudgetList();
  const { t } = useTranslation();

  const budgetHandleChange = (event: SelectChangeEvent): void => {
    setBudget(event.target.value);
  };
  const handleSearchBox = (): void => {
    setSearchboxIsShow(!searchboxIsShow);
  };
  const handleFreeWordChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    setFreeWord(event.target.value);
  };

  useEffect(() => {
    window.matchMedia('(max-width: 960px)').matches
      ? setSearchboxIsShow(false)
      : setSearchboxIsShow(true);
  }, [window.matchMedia]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [selectOption, setSelectOption] = React.useState<SelectOption[]>([]);
  const [selectOptions, setSelectOptions] = React.useState<SelectOptions[]>([]);
  const [demographic, setDemographic] = React.useState<boolean>(false);
  const percentOption = percentList();

  // モーダルの表示と中身の設定
  const handleModal = (type: string): void => {
    switch (type) {
      case 'genre':
        setTitle(t('media.genre'));
        setSelectOption(genre);
        setSelectOptions([]);
        setDemographic(false);
        break;
      case 'target':
        setTitle(t('media.target'));
        setSelectOption(target);
        setSelectOptions([]);
        setDemographic(false);
        break;
      case 'mediaType':
        setTitle(t('media.mediaType'));
        setSelectOption([]);
        setSelectOptions(mediaType);
        setDemographic(false);
        break;
      case 'industry':
        setTitle(t('media.industry'));
        setSelectOption(industry);
        setSelectOptions([]);
        setDemographic(false);
        break;
      case 'demographic':
        setTitle(t('media.demographic'));
        setSelectOption([]);
        setSelectOptions([]);
        setDemographic(true);
        setGender(selectedGender?.gender);
        setGenderPercentFrom(selectedGender?.percentFrom ?? 0);
        setGenderPercentTo(selectedGender?.percentTo ?? 0);
        setGeneration(selectedGeneration?.generation);
        setGenerationPercentFrom(selectedGeneration?.percentFrom ?? 0);
        setGenerationPercentTo(selectedGeneration?.percentTo ?? 0);
        setAccess(selectedAccess?.access);
        setAccessPercentFrom(selectedAccess?.percentFrom ?? 0);
        setAccessPercentTo(selectedAccess?.percentTo ?? 0);
        break;
      default:
        break;
    }
    setModalOpen(true);
  };

  // モーダルを非表示
  const modalClose = (): void => {
    setModalOpen(false);
  };

  // 一覧で選択済み項目の保持
  const [genre, setGenre] = React.useState<SelectOption[]>(categoryList());
  const [target, setTarget] = React.useState<SelectOption[]>(mediaTargetList());
  const [mediaType, setMediaType] = React.useState<SelectOptions[]>(
    mediaTypeList()
  );
  const [industry, setIndustry] = React.useState<SelectOption[]>(useIndustry());
  const [funnel, setFunnel] = React.useState<SelectOption[]>(funnelList());
  const [targetType, setTargetType] = React.useState<SelectOption[]>(
    targetTypeList()
  );

  const [selectedGender, setSelectedGender] = React.useState<{
    gender: string | null | undefined;
    percentFrom: number | null | undefined;
    percentTo: number | null | undefined;
  }>();
  const [selectedGeneration, setSelectedGeneration] = React.useState<{
    generation: string | null | undefined;
    percentFrom: number | null | undefined;
    percentTo: number | null | undefined;
  }>();
  const [selectedAccess, setSelectedAccess] = React.useState<{
    access: number | null | undefined;
    percentFrom: number | null | undefined;
    percentTo: number | null | undefined;
  }>();

  // 一覧から項目削除したときの処理
  const handleDelete = (value: number, type: string): void => {
    switch (type) {
      case 'genre':
        setGenre(
          genre.map((item) =>
            item.value === value ? { ...item, checked: false } : item
          )
        );
        setSelectedGenre(selectedGenre.filter((item) => item !== value));
        setSelectGenreAll(false);
        break;
      case 'target':
        setTarget(
          target.map((item) =>
            item.value === value ? { ...item, checked: false } : item
          )
        );
        setSelectedTarget(selectedTarget.filter((item) => item !== value));
        setSelectTargetAll(false);
        break;
      case 'mediaType':
        setMediaType(
          mediaType.map((item) => ({
            ...item,
            options: item.options.map((i) =>
              i.value === value ? { ...i, checked: false } : i
            )
          }))
        );
        setSelectedMediaType(
          selectedMediaType.filter((item) => item !== value)
        );
        setSelectMediaTypeAll(false);
        break;
      case 'industry':
        setIndustry(
          industry.map((item) =>
            item.value === value ? { ...item, checked: false } : item
          )
        );
        setSelectedIndustry(selectedIndustry.filter((item) => item !== value));
        setSelectIndustryAll(false);
        break;
      default:
        break;
    }
  };

  // モーダル内で選択したチェックボックスの値を配列で保持
  const [selectedGenre, setSelectedGenre] = React.useState<number[]>([]);
  const [selectedTarget, setSelectedTarget] = React.useState<number[]>([]);
  const [selectedMediaType, setSelectedMediaType] = React.useState<number[]>(
    []
  );
  const [selectedIndustry, setSelectedIndustry] = React.useState<number[]>([]);

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ): void => {
    if (type === t('media.genre')) {
      event.target.checked
        ? setSelectedGenre([...selectedGenre, Number(event.target.id)])
        : setSelectedGenre(
            selectedGenre.filter((item) => item !== Number(event.target.id))
          );
    } else if (type === t('media.target')) {
      event.target.checked
        ? setSelectedTarget([...selectedTarget, Number(event.target.id)])
        : setSelectedTarget(
            selectedTarget.filter((item) => item !== Number(event.target.id))
          );
    } else if (type === t('media.mediaType')) {
      event.target.checked
        ? setSelectedMediaType([...selectedMediaType, Number(event.target.id)])
        : setSelectedMediaType(
            selectedMediaType.filter((item) => item !== Number(event.target.id))
          );
    } else if (type === t('media.industry')) {
      event.target.checked
        ? setSelectedIndustry([...selectedIndustry, Number(event.target.id)])
        : setSelectedIndustry(
            selectedIndustry.filter((item) => item !== Number(event.target.id))
          );
    }
  };

  // モーダルのチェックボックスOn/Off
  const checkedHandle = (value: number, type: string): boolean => {
    if (type === t('media.genre')) {
      return selectedGenre.includes(value);
    } else if (type === t('media.target')) {
      return selectedTarget.includes(value);
    } else if (type === t('media.mediaType')) {
      return selectedMediaType.includes(value);
    } else if (type === t('media.industry')) {
      return selectedIndustry.includes(value);
    }
    return false;
  };

  // モーダルで選択した項目を一覧に反映
  const sendSelectedValue = (type: string): void => {
    switch (type) {
      case t('media.genre'):
        setGenre(
          genre.map((item) => ({
            ...item,
            checked: selectedGenre.includes(item.value)
          }))
        );
        break;
      case t('media.target'):
        setTarget(
          target.map((item) => ({
            ...item,
            checked: selectedTarget.includes(item.value)
          }))
        );
        break;
      case t('media.mediaType'):
        setMediaType(
          mediaType.map((item) => ({
            ...item,
            options: item.options.map((i) => ({
              ...i,
              checked: selectedMediaType.includes(i.value)
            }))
          }))
        );
        break;
      case t('media.industry'):
        setIndustry(
          industry.map((item) => ({
            ...item,
            checked: selectedIndustry.includes(item.value)
          }))
        );
        break;
      case t('media.demographic'):
        setSelectedGender({
          gender,
          percentFrom: genderPercentFrom,
          percentTo: genderPercentTo
        });
        setSelectedGeneration({
          generation,
          percentFrom: generationPercentFrom,
          percentTo: generationPercentTo
        });
        setSelectedAccess({
          access,
          percentFrom: accessPercentFrom,
          percentTo: accessPercentTo
        });
        break;
      default:
        break;
    }
    setModalOpen(false);
  };

  // すべてを選択/解除した場合の処理
  const [selectGenreAll, setSelectGenreAll] = React.useState<boolean>(false);
  const [selectTargetAll, setSelectTargetAll] = React.useState<boolean>(false);
  const [selectMediaTypeAll, setSelectMediaTypeAll] =
    React.useState<boolean>(false);
  const [selectIndustryAll, setSelectIndustryAll] =
    React.useState<boolean>(false);

  const allChecked = (type: string): boolean => {
    if (type === t('media.genre')) {
      return selectGenreAll;
    } else if (type === t('media.target')) {
      return selectTargetAll;
    } else if (type === t('media.mediaType')) {
      return selectMediaTypeAll;
    } else if (type === t('media.industry')) {
      return selectIndustryAll;
    }
    return false;
  };

  const handleSelectAllChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ): void => {
    switch (type) {
      case t('media.genre'):
        setSelectGenreAll(e.target.checked);
        setSelectedGenre(
          e.target.checked ? genre.map((item) => item.value) : []
        );
        break;
      case t('media.target'):
        setSelectTargetAll(e.target.checked);
        setSelectedTarget(
          e.target.checked ? target.map((item) => item.value) : []
        );
        break;
      case t('media.mediaType'):
        setSelectMediaTypeAll(e.target.checked);
        setSelectedMediaType(
          e.target.checked
            ? mediaType.flatMap((item) => item.options.map((i) => i.value))
            : []
        );
        break;
      case t('media.industry'):
        setSelectIndustryAll(e.target.checked);
        setSelectedIndustry(
          e.target.checked ? industry.map((item) => item.value) : []
        );
        break;
      default:
        break;
    }
  };

  const handleSelectChip = (value: number, type: string): void => {
    if (type === 'targetType') {
      setTargetType(
        targetType.map((item) =>
          item.value === value ? { ...item, checked: true } : item
        )
      );
    } else if (type === 'funnel') {
      setFunnel(
        funnel.map((item) =>
          item.value === value ? { ...item, checked: true } : item
        )
      );
    }
  };

  const handleDeleteChip = (value: number, type: string): void => {
    if (type === 'targetType') {
      setTargetType(
        targetType.map((item) =>
          item.value === value ? { ...item, checked: false } : item
        )
      );
    } else if (type === 'funnel') {
      setFunnel(
        funnel.map((item) =>
          item.value === value ? { ...item, checked: false } : item
        )
      );
    }
  };

  // デモグラの検索
  const generationItemList = generationList();
  const accessItemList = accessList();
  const [gender, setGender] = React.useState<string | null | undefined>(null);
  const [generation, setGeneration] = React.useState<string | null | undefined>(
    null
  );
  const [access, setAccess] = React.useState<number | null | undefined>(null);
  const [genderPercentFrom, setGenderPercentFrom] = React.useState<
    number | null | undefined
  >(0);
  const [genderPercentTo, setGenderPercentTo] = React.useState<
    number | null | undefined
  >(0);
  const [generationPercentFrom, setGenerationPercentFrom] = React.useState<
    number | null | undefined
  >(0);
  const [generationPercentTo, setGenerationPercentTo] = React.useState<
    number | null | undefined
  >(0);
  const [accessPercentFrom, setAccessPercentFrom] = React.useState<
    number | null | undefined
  >(0);
  const [accessPercentTo, setAccessPercentTo] = React.useState<
    number | null | undefined
  >(0);

  // 割合をselectした時の処理
  const handlePercentChange = (
    event: SelectChangeEvent<number | null>,
    item: string,
    type: string
  ): void => {
    switch (item) {
      case 'gender':
        type === 'from'
          ? setGenderPercentFrom(Number(event.target.value))
          : setGenderPercentTo(Number(event.target.value));
        break;
      case 'generation':
        type === 'from'
          ? setGenerationPercentFrom(Number(event.target.value))
          : setGenerationPercentTo(Number(event.target.value));
        break;
      case 'access':
        type === 'from'
          ? setAccessPercentFrom(Number(event.target.value))
          : setAccessPercentTo(Number(event.target.value));
        break;
      default:
        break;
    }
  };

  // 検索条件をクリア
  const handleAllClear = (): void => {
    setGenre(genre.map((item) => ({ ...item, checked: false })));
    setSelectedGenre([]);
    setSelectGenreAll(false);
    setTarget(target.map((item) => ({ ...item, checked: false })));
    setSelectedTarget([]);
    setSelectTargetAll(false);
    setMediaType(
      mediaType.map((item) => ({
        ...item,
        options: item.options.map((i) => ({ ...i, checked: false }))
      }))
    );
    setSelectedMediaType([]);
    setSelectMediaTypeAll(false);
    setIndustry(industry.map((item) => ({ ...item, checked: false })));
    setSelectedIndustry([]);
    setSelectIndustryAll(false);
    setTargetType(targetType.map((item) => ({ ...item, checked: false })));
    setFunnel(funnel.map((item) => ({ ...item, checked: false })));
    setBudget('');
    setFreeWord('');
    setSelectedGender({
      gender: null,
      percentFrom: null,
      percentTo: null
    });
    setSelectedGeneration({
      generation: null,
      percentFrom: null,
      percentTo: null
    });
    setSelectedAccess({
      access: null,
      percentFrom: null,
      percentTo: null
    });
  };

  // 検索テーブルの項目と選択ボックスの設定
  const searchItems = [
    {
      name: t('media.genre'),
      option: (
        <Typography component="div" sx={styles.buttonAndChips}>
          <Button sx={styles.tableButton} onClick={() => handleModal('genre')}>
            ＋ {t('media.searchGenre')}
          </Button>
          {genre.map((item, index) =>
            item.checked ? (
              <Chip
                key={index}
                sx={styles.selectedChip}
                label={item.option}
                onDelete={() => handleDelete(item.value, 'genre')}
              />
            ) : (
              ''
            )
          )}
        </Typography>
      )
    },
    {
      name: t('media.mediaType'),
      option: (
        <Typography component="div" sx={styles.buttonAndChips}>
          <Button
            sx={styles.tableButton}
            onClick={() => handleModal('mediaType')}
          >
            ＋ {t('media.searchMediaType')}
          </Button>
          {mediaType.map((item, index) =>
            item.options
              .filter((option) => option.checked)
              .map((option, optionIndex) => (
                <Chip
                  key={`${index}-${optionIndex}`}
                  sx={styles.selectedChip}
                  label={option.option}
                  onDelete={() => handleDelete(option.value, 'mediaType')}
                />
              ))
          )}
        </Typography>
      )
    },
    {
      guideText: t('guideText.media.funnel'),
      name: t('media.funnel'),
      option: (
        <Typography component="div" sx={styles.chips}>
          {funnel.map((item, index) => (
            <Chip
              sx={
                item.checked
                  ? styles.selectedChip
                  : {
                      '&.MuiChip-root .MuiChip-deleteIcon': {
                        display: 'none'
                      }
                    }
              }
              key={index}
              label={item.option}
              onClick={() => handleSelectChip(item.value, 'funnel')}
              onDelete={() => handleDeleteChip(item.value, 'funnel')}
              {...(!item.checked && { ...{ variant: 'outlined' } })}
            />
          ))}
        </Typography>
      )
    },
    {
      name: t('media.target'),
      option: (
        <Typography component="div" sx={styles.buttonAndChips}>
          <Button sx={styles.tableButton} onClick={() => handleModal('target')}>
            ＋ {t('media.searchTarget')}
          </Button>
          {target.map((item, index) =>
            item.checked ? (
              <Chip
                key={index}
                sx={[styles.selectedChip]}
                label={item.option}
                onDelete={() => handleDelete(item.value, 'target')}
              />
            ) : (
              ''
            )
          )}
        </Typography>
      )
    },
    {
      name: t('media.targetType'),
      option: (
        <Typography component="div" flexDirection="row" sx={styles.chips}>
          {targetType.map((item, index) => (
            <Chip
              sx={
                item.checked
                  ? styles.selectedChip
                  : {
                      '&.MuiChip-root .MuiChip-deleteIcon': {
                        display: 'none'
                      }
                    }
              }
              key={index}
              label={item.option}
              onClick={() => handleSelectChip(item.value, 'targetType')}
              onDelete={() => handleDeleteChip(item.value, 'targetType')}
              {...(!item.checked && { ...{ variant: 'outlined' } })}
            />
          ))}
        </Typography>
      )
    },
    {
      name: t('media.demographic'),
      option: (
        <Typography component="div" sx={styles.buttonAndChips}>
          <Button
            sx={styles.tableButton}
            onClick={() => handleModal('demographic')}
          >
            ＋ {t('media.searchDemographic')}
          </Button>
          {selectedGender?.gender && (
            <Chip
              sx={[styles.selectedChip]}
              label={`${
                (selectedGender?.gender === 'male'
                  ? t('media.demoTable.male')
                  : t('media.demoTable.female')) ?? ''
              }： ${
                selectedGender?.percentFrom !== null &&
                selectedGender?.percentFrom !== undefined
                  ? `${selectedGender.percentFrom}％`
                  : ''
              } 〜 ${
                selectedGender?.percentTo !== null &&
                selectedGender?.percentTo !== undefined
                  ? `${selectedGender.percentTo}％`
                  : ''
              }`}
              onDelete={() =>
                setSelectedGender({
                  gender: null,
                  percentFrom: null,
                  percentTo: null
                })
              }
            />
          )}
          {selectedGeneration?.generation && (
            <Chip
              sx={[styles.selectedChip]}
              label={`${
                selectedGeneration?.generation
                  ? generationItemList.find(
                      (option) =>
                        option.value === selectedGeneration?.generation
                    )?.option ?? ''
                  : ''
              }：${
                selectedGeneration?.percentFrom !== null &&
                selectedGeneration?.percentFrom !== undefined
                  ? `${selectedGeneration.percentFrom}％`
                  : ''
              } 〜 ${
                selectedGeneration?.percentTo !== null &&
                selectedGeneration?.percentTo !== undefined
                  ? `${selectedGeneration.percentTo}％`
                  : ''
              }`}
              onDelete={() =>
                setSelectedGeneration({
                  generation: null,
                  percentFrom: null,
                  percentTo: null
                })
              }
            />
          )}
          {selectedAccess?.access !== null &&
            selectedAccess?.access !== undefined &&
            selectedAccess?.access !== 0 && (
              <Chip
                sx={[styles.selectedChip]}
                label={`${
                  selectedAccess?.access
                    ? accessItemList.find(
                        (option) => option.value === selectedAccess?.access
                      )?.option ?? ''
                    : ''
                }：${
                  selectedAccess?.percentFrom !== null &&
                  selectedAccess?.percentFrom !== undefined
                    ? `${selectedAccess.percentFrom}％`
                    : ''
                } 〜 ${
                  selectedAccess?.percentTo !== null &&
                  selectedAccess?.percentTo !== undefined
                    ? `${selectedAccess.percentTo}％`
                    : ''
                }`}
                onDelete={() =>
                  setSelectedAccess({
                    access: null,
                    percentFrom: null,
                    percentTo: null
                  })
                }
              />
            )}
        </Typography>
      )
    },
    {
      guideText: t('guideText.media.main'),
      name: t('media.industry'),
      option: (
        <Typography component="div" sx={styles.buttonAndChips}>
          <Button
            sx={styles.tableButton}
            onClick={() => handleModal('industry')}
          >
            ＋ {t('media.searchIndustry')}
          </Button>
          {industry.map((item, index) =>
            item.checked ? (
              <Chip
                key={index}
                sx={styles.selectedChip}
                label={item.option}
                onDelete={() => handleDelete(item.value, 'industry')}
              />
            ) : (
              ''
            )
          )}
        </Typography>
      )
    },
    {
      name: t('media.budget'),
      option: (
        <FormControl sx={{ m: 0, width: '100%' }}>
          <Select
            value={budget}
            onChange={budgetHandleChange}
            displayEmpty
            sx={styles.select}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            <MenuItem value={''} sx={{ color: black }}>
              <Typography
                component="p"
                sx={{ color: grayPlaceholder, textAlign: 'left' }}
              >
                {'選択してください'}
              </Typography>
            </MenuItem>
            {budgetList.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    },
    {
      name: t('media.searchFreeWord'),
      option: (
        <FormControl sx={{ m: 0, width: '100%' }}>
          <OutlinedInput
            onChange={handleFreeWordChange}
            placeholder={t('media.searchFreeWordPlaceholder')}
            sx={styles.select}
            value={freeWord}
          />
        </FormControl>
      )
    }
  ];

  return (
    <>
      <Card sx={[styles.card, styles.cardBorder]}>
        <Typography sx={styles.cardContents} align="left" component="div">
          <Button sx={styles.searchSpButton} onClick={handleSearchBox}>
            <Typography component="p">{t('media.searchBlock')}</Typography>
            {searchboxIsShow ? <RemoveIcon /> : <AddIcon />}
          </Button>
          <Typography
            component="div"
            sx={[
              styles.searchBox,
              searchboxIsShow ? styles.isActive : styles.isNotActive
            ]}
          >
            <Grid container>
              {searchItems.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Grid container sx={styles.tableRow}>
                    <Grid
                      item
                      sx={[
                        styles.tableItem,
                        {
                          alignItems: 'center',
                          display: 'flex',
                          flexDirection: 'row'
                        }
                      ]}
                    >
                      <Typography component="div" sx={{ fontSize: '14px' }}>
                        {item.name}
                      </Typography>
                      {item.guideText && (
                        <CustomGuideButton
                          title={item.name}
                          guideText={item.guideText}
                        />
                      )}
                    </Grid>
                    <Grid item sx={styles.tableValue}>
                      {item.option}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
            <Grid
              container
              flexDirection="row"
              justifyContent="flex-end"
              sx={styles.buttons}
            >
              <Button
                sx={[
                  styles.button,
                  styles.clearButton,
                  styles.tertiaryButtonBackground
                ]}
                variant="outlined"
                onClick={() => {
                  handleAllClear();
                  search(
                    '',
                    genre.map((item) => ({ ...item, checked: false })),
                    mediaType.map((item) => ({
                      ...item,
                      options: item.options.map((i) => ({
                        ...i,
                        checked: false
                      }))
                    })),
                    funnel.map((item) => ({ ...item, checked: false })),
                    target.map((item) => ({ ...item, checked: false })),
                    targetType.map((item) => ({ ...item, checked: false })),
                    {
                      gender: null,
                      percentFrom: null,
                      percentTo: null
                    },
                    {
                      generation: null,
                      percentFrom: null,
                      percentTo: null
                    },
                    {
                      access: null,
                      percentFrom: null,
                      percentTo: null
                    },
                    industry,
                    ''
                  );
                }}
              >
                {t('media.clear')}
              </Button>
              <Button
                sx={[styles.button, styles.blueButton]}
                variant="contained"
                onClick={() =>
                  search(
                    freeWord,
                    genre,
                    mediaType,
                    funnel,
                    target,
                    targetType,
                    selectedGender,
                    selectedGeneration,
                    selectedAccess,
                    industry,
                    budget
                  )
                }
              >
                {t('media.search')}
              </Button>
            </Grid>
          </Typography>
        </Typography>
      </Card>

      {/* モーダル */}
      <Dialog onClose={modalClose} open={modalOpen} sx={styles.modalWrapper}>
        <DialogContent sx={styles.modal}>
          <Typography component="div" sx={styles.modalTitle}>
            <DialogTitle sx={styles.modalText}>{title}</DialogTitle>
            <CloseIcon onClick={modalClose} sx={{ cursor: 'pointer' }} />
          </Typography>
          {!demographic && (
            <FormGroup sx={styles.modalOptions}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allChecked(title)}
                    onChange={(e) => handleSelectAllChange(e, title)}
                  />
                }
                label={`${t('media.all')}${title}${t('media.select')}`}
                sx={[styles.modalOption, styles.modalOptionAllWidth]}
              />
              {selectOption
                ? selectOption.map((item, index) => (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={checkedHandle(item.value, title)}
                          onChange={(e) => {
                            handleCheckboxChange(e, title);
                          }}
                          inputProps={{ id: item.value.toString() }}
                        />
                      }
                      label={item.option}
                      sx={[styles.modalOption, styles.modalOptionWidth]}
                    />
                  ))
                : ''}
              {selectOptions
                ? selectOptions.map((item, index) => (
                    <Grid key={index}>
                      <Typography component="p" sx={[blackText, styles.text]}>
                        {item.name}
                      </Typography>
                      <Grid container sx={[styles.modalOptions, { mb: 0 }]}>
                        {item.options.map((option, optionIndex) => (
                          <FormControlLabel
                            key={optionIndex}
                            control={
                              <Checkbox
                                checked={checkedHandle(option.value, title)}
                                onChange={(e) => {
                                  handleCheckboxChange(e, title);
                                }}
                                inputProps={{ id: option.value.toString() }}
                              />
                            }
                            label={option.option}
                            sx={[styles.modalOption, styles.modalOptionWidth]}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  ))
                : ''}
            </FormGroup>
          )}

          {/* デモグラ */}
          {demographic && (
            <Grid container sx={styles.demoTableWidth}>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid
                  item
                  sx={styles.demoTableTextbg}
                  container
                  alignItems="center"
                >
                  <Typography component="p" sx={styles.demoTableText}>
                    {t('media.demoTable.gender')}
                  </Typography>
                </Grid>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid
                    item
                    xs={12}
                    sx={[
                      styles.demoTablePadding,
                      { borderBottom: '1px solid #DBDBE0' }
                    ]}
                  >
                    <RadioGroup
                      row
                      aria-labelledby="demo-form-control-label-placement"
                      name="position"
                      sx={{
                        '&.MuiFormGroup-root': { alignContent: 'flex-start' }
                      }}
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <Grid container flexDirection="row">
                        <FormControlLabel
                          value="male"
                          control={<Radio sx={{ color: gray }} />}
                          label={t('media.demoTable.male')}
                          sx={[blackText, styles.demoTableOption]}
                        />
                        <FormControlLabel
                          value="female"
                          control={<Radio sx={{ color: gray }} />}
                          label={t('media.demoTable.female')}
                          sx={[blackText, styles.demoTableOption]}
                        />
                      </Grid>
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} sx={styles.demoTablePercents}>
                    <Grid container>
                      <Grid item>
                        <Select
                          value={genderPercentFrom}
                          onChange={(e) =>
                            handlePercentChange(e, 'gender', 'from')
                          }
                          displayEmpty
                          sx={styles.selectPercent}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value={0} sx={{ color: black }}>
                            <Typography
                              component="p"
                              sx={{ color: grayPlaceholder, textAlign: 'left' }}
                            >
                              {t('media.demoTable.percent')}
                            </Typography>
                          </MenuItem>
                          {percentOption.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item sx={styles.demoTablePercentBetween}>
                        〜
                      </Grid>
                      <Grid item>
                        <Select
                          value={genderPercentTo}
                          onChange={(e) =>
                            handlePercentChange(e, 'gender', 'to')
                          }
                          displayEmpty
                          sx={styles.selectPercent}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value={0} sx={{ color: black }}>
                            <Typography
                              component="p"
                              sx={{ color: grayPlaceholder, textAlign: 'left' }}
                            >
                              {t('media.demoTable.percent')}
                            </Typography>
                          </MenuItem>
                          {percentOption.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid
                  item
                  sx={styles.demoTableTextbg}
                  container
                  alignItems="center"
                >
                  <Typography component="p" sx={styles.demoTableText}>
                    {t('media.demoTable.generation')}
                  </Typography>
                </Grid>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid
                    item
                    xs={12}
                    sx={[
                      styles.demoTablePadding,
                      { borderBottom: '1px solid #DBDBE0' }
                    ]}
                  >
                    <RadioGroup
                      row
                      aria-labelledby="demo-form-control-label-placement"
                      name="position"
                      sx={{
                        '&.MuiFormGroup-root': { alignContent: 'flex-start' }
                      }}
                      value={generation}
                      onChange={(e) => setGeneration(e.target.value)}
                    >
                      <Grid container flexDirection="row">
                        {generationItemList.map((item) => (
                          <FormControlLabel
                            key={item.value}
                            value={item.value}
                            control={<Radio sx={{ color: gray }} />}
                            label={item.option}
                            sx={[blackText, styles.demoTableOption]}
                          />
                        ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} sx={styles.demoTablePercents}>
                    <Grid container>
                      <Grid item>
                        <Select
                          value={generationPercentFrom}
                          onChange={(e) =>
                            handlePercentChange(e, 'generation', 'from')
                          }
                          displayEmpty
                          sx={styles.selectPercent}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value={0} sx={{ color: black }}>
                            <Typography
                              component="p"
                              sx={{ color: grayPlaceholder, textAlign: 'left' }}
                            >
                              {t('media.demoTable.percent')}
                            </Typography>
                          </MenuItem>
                          {percentOption.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item sx={styles.demoTablePercentBetween}>
                        〜
                      </Grid>
                      <Grid item>
                        <Select
                          value={generationPercentTo}
                          onChange={(e) =>
                            handlePercentChange(e, 'generation', 'to')
                          }
                          displayEmpty
                          sx={styles.selectPercent}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value={0} sx={{ color: black }}>
                            <Typography
                              component="p"
                              sx={{ color: grayPlaceholder, textAlign: 'left' }}
                            >
                              {t('media.demoTable.percent')}
                            </Typography>
                          </MenuItem>
                          {percentOption.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid
                  item
                  sx={styles.demoTableTextbg}
                  container
                  alignItems="center"
                >
                  <Typography component="p" sx={styles.demoTableText}>
                    {t('media.demoTable.access')}
                  </Typography>
                </Grid>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid
                    item
                    xs={12}
                    sx={[
                      styles.demoTablePadding,
                      { borderBottom: '1px solid #DBDBE0' }
                    ]}
                  >
                    <RadioGroup
                      row
                      aria-labelledby="demo-form-control-label-placement"
                      name="position"
                      sx={{
                        '&.MuiFormGroup-root': { alignContent: 'flex-start' }
                      }}
                      value={access}
                      onChange={(e) => setAccess(Number(e.target.value))}
                    >
                      <Grid container flexDirection="row">
                        {accessItemList.map((item) => (
                          <FormControlLabel
                            key={item.value}
                            value={item.value}
                            control={<Radio sx={{ color: gray }} />}
                            label={item.option}
                            sx={[blackText, styles.demoTableOption]}
                          />
                        ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} sx={styles.demoTablePercents}>
                    <Grid container>
                      <Grid item>
                        <Select
                          value={accessPercentFrom}
                          onChange={(e) =>
                            handlePercentChange(e, 'access', 'from')
                          }
                          displayEmpty
                          sx={styles.selectPercent}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value={0} sx={{ color: black }}>
                            <Typography
                              component="p"
                              sx={{ color: grayPlaceholder, textAlign: 'left' }}
                            >
                              {t('media.demoTable.percent')}
                            </Typography>
                          </MenuItem>
                          {percentOption.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid item sx={styles.demoTablePercentBetween}>
                        〜
                      </Grid>
                      <Grid item>
                        <Select
                          value={accessPercentTo}
                          onChange={(e) =>
                            handlePercentChange(e, 'access', 'to')
                          }
                          displayEmpty
                          sx={styles.selectPercent}
                          inputProps={{ 'aria-label': 'Without label' }}
                        >
                          <MenuItem value={0} sx={{ color: black }}>
                            <Typography
                              component="p"
                              sx={{ color: grayPlaceholder, textAlign: 'left' }}
                            >
                              {t('media.demoTable.percent')}
                            </Typography>
                          </MenuItem>
                          {percentOption.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Button
            sx={[styles.blueButton, styles.modalButton]}
            onClick={() => {
              sendSelectedValue(title);
            }}
          >
            {t('media.decision')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaSearchCard;
